import { Check, ChevronDown, X } from 'lucide-react';
import { orgToday } from '@/lib/today';
import * as React from 'react';
import { formatDuration } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { TasksArt } from '@/components/illustrations';
import { ConfirmButton } from '@/components/confirm-dialog';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { SubmissionBoard } from '@/components/reporting/submission-board';
import { WeekGrid } from '@/components/timesheet/week-grid';
import { Hint } from '@/components/cargofl-ui';
import { toast } from 'sonner';

import { ApiError, toastFailure } from '@/lib/api';
import { useWeekGrid } from '@/lib/grid-api';
import {
  useApproveWeek,
  useBulkApprove,
  useDecidedWeeks,
  useDenyEmptyWeeks,
  useDenyWeek,
  usePendingApprovals,
  useReopenWeek,
  type DecidedWeek,
  type PendingWeek,
} from '@/lib/approvals-api';

/**
 * The approval queue.
 *
 * A worklist, not a report: oldest first, because the week somebody has been
 * waiting on longest is the one holding them up. The server already returns only
 * the weeks this person may decide, so nothing here can act on someone else's team.
 */
export function ApprovalsPage() {
  const { data: pending, isPending } = usePendingApprovals();
  const approve = useApproveWeek();
  const bulk = useBulkApprove();
  const denyEmpty = useDenyEmptyWeeks();

  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [personId, setPersonId] = React.useState<string | null>(null);
  const [denying, setDenying] = React.useState<PendingWeek | null>(null);
  /** The week whose entries are open below its row. One at a time. */
  const [open, setOpen] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Selections are dropped whenever the list changes: an id that has just been
  // decided is no longer selectable, and keeping it would send a doomed request.
  React.useEffect(() => setSelected(new Set()), [pending]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Filtered here rather than on the server: the queue is a few hundred rows that
  // are already loaded, and a request per keystroke would be slower than the filter.
  const all = pending ?? [];

  const people = React.useMemo(() => {
    const byId = new Map<string, { id: string; name: string; weeks: number }>();
    for (const week of all) {
      const found = byId.get(week.employee.id);
      if (found) found.weeks += 1;
      else byId.set(week.employee.id, { id: week.employee.id, name: week.employee.displayName, weeks: 1 });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const rows = personId === null ? all : all.filter((w) => w.employee.id === personId);
  const allSelected = rows.length > 0 && selected.size === rows.length;

  // Counted here as well as on the server so the button can say how many, and can
  // stay hidden when there is nothing to clear. The server decides which weeks are
  // actually sent back.
  const emptyWeeks = rows.filter(
    // en-CA gives YYYY-MM-DD in the viewer's own zone. toISOString would give UTC,
    // which before 05:30 IST is yesterday.
    (week) => week.actualSeconds === 0 && week.periodEnd < orgToday(),
  ).length;

  const runDenyEmpty = async () => {
    setError(null);
    try {
      const result = await denyEmpty.mutateAsync();
      if (result.failed.length > 0) {
        const m = `${result.denied} sent back. ${result.failed.length} could not be: ${result.failed[0]}`;
        setError(m);
        toast.error(m);
      }
    } catch (err) {
      const m = err instanceof Error ? err.message : 'Could not send those weeks back.';
      setError(m);
      toast.error(`Could not send those weeks back: ${m}`);
    }
  };

  const runBulk = async () => {
    setError(null);
    try {
      const result = await bulk.mutateAsync([...selected]);
      if (result.failed.length > 0) {
        // Reported rather than swallowed: a partial success where some weeks were
        // refused is exactly the case somebody needs to know about.
        const m = `Approved ${result.approved}. ${result.failed.length} could not be approved: ${result.failed[0]?.message ?? ''}`;
        setError(m);
        toast.error(m);
      }
    } catch (err) {
      const m = err instanceof ApiError ? err.message : 'Could not approve those weeks.';
      setError(m);
      toast.error(`Could not approve those weeks: ${m}`);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Approvals"
        description="Weeks your team has submitted, and the weeks they have not."
        actions={
          selected.size > 0 ? (
            <ConfirmButton
              disabled={bulk.isPending}
              pending={bulk.isPending}
              title={`Approve ${selected.size} week${selected.size === 1 ? '' : 's'}?`}
              description={
                <>
                  {/* Named as somebody else's record, because that is what it is.
                      Reopening one afterwards is a second deliberate act. */}
                  Each week is signed off under your name and locked, and the person is
                  emailed. Reopening one afterwards takes a separate action, so it is
                  worth having looked at the totals first.
                </>
              }
              confirmLabel={`Approve ${selected.size}`}
              onConfirm={() => void runBulk()}
            >
              <Check className="size-4" />
              {bulk.isPending ? 'Approving…' : `Approve ${selected.size} selected`}
            </ConfirmButton>
          ) : emptyWeeks > 0 ? (
            // Only offered when there is actually something to clear, and it says how
            // many so the click is not a guess. An empty week reaches the queue when
            // somebody submits out of habit on a Friday without recording anything;
            // approving it would sign off a week of no work.
            <ConfirmButton
              variant="outline"
              disabled={denyEmpty.isPending}
              pending={denyEmpty.isPending}
              title={`Send back ${emptyWeeks} empty week${emptyWeeks === 1 ? '' : 's'}?`}
              description={
                <>
                  These weeks are over and have no hours recorded at all. Each is sent
                  back and unlocked, and the person is emailed asking them to fill it in.
                  Weeks with any time in them are not touched.
                </>
              }
              confirmLabel="Send them back"
              destructive
              onConfirm={() => void runDenyEmpty()}
            >
              {denyEmpty.isPending
                ? 'Sending back…'
                : `Send back ${emptyWeeks} empty week${emptyWeeks === 1 ? '' : 's'}`}
            </ConfirmButton>
          ) : undefined
        }
      />

      {/* Above the queue on purpose. The queue is what people sent; this is what
          they did not, and the second question is the one nobody could ask. */}
      <details className="rounded-lg border" open>
        <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium">
          Who is behind
        </summary>
        <div className="border-t p-4">
          <SubmissionBoard />
        </div>
      </details>

      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-64 w-full" />}

      {pending && rows.length === 0 && (
        <Card>
          <CardContent>
            <EmptyState art={TasksArt} title="Nothing is waiting on you" body="Weeks appear here once your team submits them." />
          </CardContent>
        </Card>
      )}

      {/* Search a person and work only their weeks. With a queue of several
          hundred, scrolling for one name is the slow part. */}
      {people.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full max-w-sm">
            <Combobox
              value={personId}
              onChange={(v) => {
                setPersonId(v);
                // A selection made across everybody means nothing once filtered.
                setSelected(new Set());
              }}
              options={people.map((p) => ({
                value: p.id,
                label: `${p.name} · ${p.weeks} ${p.weeks === 1 ? 'week' : 'weeks'}`,
              }))}
              placeholder="Everyone"
              searchPlaceholder="Search for a person"
            />
          </div>
          {personId !== null && (
            <Button hint={"Show every person's weeks, not only those waiting for you"} variant="ghost" size="sm" onClick={() => { setPersonId(null); setSelected(new Set()); }}>
              Show everyone
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {rows.length} of {all.length} {all.length === 1 ? 'week' : 'weeks'}
          </span>
        </div>
      )}

      {rows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[780px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        aria-label="Select all"
                        onCheckedChange={(v) =>
                          setSelected(v === true ? new Set(rows.map((r) => r.id)) : new Set())
                        }
                      />
                    </TableHead>
                    <TableHead>Person</TableHead>
                    <TableHead>Week</TableHead>
                    <TableHead className="text-right">Recorded</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((week) => (
                    <React.Fragment key={week.id}>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(week.id)}
                          aria-label={`Select ${week.employee.displayName}`}
                          onCheckedChange={() => toggle(week.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {/* Opens the week itself. Approving on a total alone is
                            approving blind - the question is almost never "is 40
                            hours right" but "40 hours of WHAT". */}
                        <Hint label={"Open this person's week"}><button
                          type="button"
                          className="flex items-center gap-1.5 text-left hover:underline"
                          onClick={() => setOpen(open === week.id ? null : week.id)}
                        >
                          <ChevronDown
                            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
                              open === week.id ? '' : '-rotate-90'
                            }`}
                          />
                          {week.employee.displayName}
                        </button></Hint>
                      </TableCell>
                      <TableCell className="ts-num text-sm text-muted-foreground">
                        {shortRange(week.periodStart, week.periodEnd)}
                      </TableCell>
                      <TableCell className="ts-num text-right font-medium">
                        {formatDuration(week.actualSeconds ?? 0)}
                      </TableCell>
                      <TableCell className="ts-num text-right text-muted-foreground">
                        <ExpectedCell seconds={week.expectedSeconds} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {week.submittedAt ? shortDate(week.submittedAt) : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button hint={"Approve this week — it locks"}
                            variant="ghost"
                            size="sm"
                            disabled={approve.isPending}
                            onClick={() => approve.mutate(week.id, { onError: toastFailure(`Could not approve ${week.employee.displayName}'s week`) })}
                          >
                            <Check className="size-4" />
                            Approve
                          </Button>
                          <Button hint={"Send this week back, with a reason"} variant="ghost" size="sm" onClick={() => setDenying(week)}>
                            <X className="size-4" />
                            Deny
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {open === week.id && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/20 p-4">
                          <WeekDetail week={week} />
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <DecidedWeeks />

      <DenyDialog week={denying} onClose={() => setDenying(null)} />
    </PageContainer>
  );
}

/**
 * Expected hours, where they are known.
 *
 * 1,356 of the migrated weeks carry zero, because the legacy system never
 * populated the figure. Rendering that as "0:00" would read as "this person was
 * expected to work nothing", so an unset value says so instead. Real expected
 * hours arrive with working contracts.
 */
function ExpectedCell({ seconds }: { seconds: number }) {
  if (seconds === 0) return <span className="opacity-50">not recorded</span>;
  return <>{formatDuration(seconds)}</>;
}

/**
 * Denial needs a reason.
 *
 * Required, not optional: being told "no" with nothing to act on sends the person
 * to their supervisor to ask what was wrong, which is the conversation the message
 * exists to save.
 */
/**
 * The submitted week, as it was entered.
 *
 * The same grid the person filled in, read-only - not a second layout over the
 * same rows. The API returns it locked because it belongs to somebody else, so the
 * component that already knows how to render a closed week does it unchanged.
 */
function WeekDetail({ week }: { week: PendingWeek }) {
  const { data, isPending } = useWeekGrid(week.weekStart, week.employee.id);

  if (isPending) return <Skeleton className="h-40 w-full" />;
  if (!data) return null;

  const balance = (week.actualSeconds ?? 0) - week.expectedSeconds;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span>
          Recorded{' '}
          <span className="ts-num font-semibold">{formatDuration(week.actualSeconds ?? 0)}</span>
        </span>
        <span className="text-muted-foreground">
          against{' '}
          <span className="ts-num">{formatDuration(week.expectedSeconds)}</span> expected
        </span>
        {/* The week's own target, snapshotted when it was submitted - not the sum
            of the contract's seven days, which overstates every week containing a
            public holiday. */}
        <span className={balance < 0 ? 'text-destructive' : 'text-primary'}>
          {balance === 0
            ? 'exactly on target'
            : `${balance < 0 ? '−' : '+'}${formatDuration(Math.abs(balance))}`}
        </span>
      </div>
      <WeekGrid grid={data} />
    </div>
  );
}

function DenyDialog({ week, onClose }: { week: PendingWeek | null; onClose: () => void }) {
  const deny = useDenyWeek();
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setMessage('');
    setError(null);
  }, [week]);

  if (!week) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (message.trim().length < 3) {
      setError('Say briefly what needs correcting.');
      return;
    }
    try {
      await deny.mutateAsync({ id: week.id, message: message.trim() });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not deny that week.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send back to {week.employee.displayName}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {shortRange(week.periodStart, week.periodEnd)} — those days reopen for editing and{' '}
            {week.employee.displayName} can resubmit it.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="deny-message">What needs correcting?</Label>
            <Textarea
              id="deny-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Thursday is booked to the wrong project."
            />
          </div>
          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button hint={"Send the week back — the person can edit it again"} type="submit" variant="destructive" disabled={deny.isPending}>
              {deny.isPending ? 'Sending back…' : 'Send back'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const UTC = { timeZone: 'UTC' } as const;

function shortRange(from: string, to: string): string {
  const f = (d: string, withYear = false) =>
    new Date(`${d}T00:00:00.000Z`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
      ...UTC,
    });
  return `${f(from)} – ${f(to, true)}`;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export { ExpectedCell };

/**
 * Weeks already decided, and the way to undo a decision.
 *
 * This section exists because the app tells people, in three separate places, to
 * "ask your supervisor to reopen it" - and there was no screen on which a
 * supervisor could find a decided week. The endpoint and the hook both existed;
 * nothing rendered them. So an approved week was locked permanently, and 840 of
 * them were.
 *
 * Collapsed by default: it is for correcting a decision somebody has questioned,
 * not something an approver needs in their face while working the queue.
 */
function DecidedWeeks() {
  const [open, setOpen] = React.useState(false);
  const { data } = useDecidedWeeks();
  const reopen = useReopenWeek();
  const [error, setError] = React.useState<string | null>(null);

  if (!data || data.length === 0) return null;

  const run = async (week: DecidedWeek) => {
    setError(null);
    try {
      await reopen.mutateAsync({ id: week.id, message: 'Reopened for correction' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reopen that week.');
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <Hint label={'Show weeks you already decided — reopen one if it needs correcting'}><button
          type="button"
          className="flex w-full items-center justify-between px-4 py-3 text-left"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="text-sm font-semibold">
            Already decided
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {data.length} recent — reopen one if it needs correcting
            </span>
          </span>
          <ChevronDown className={`size-4 transition-transform ${open ? '' : '-rotate-90'}`} />
        </button></Hint>

        {open && (
          <div className="overflow-x-auto border-t">
            {error && (
              <Alert className="m-4 border-destructive/30">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Week</TableHead>
                  <TableHead className="text-right">Recorded</TableHead>
                  <TableHead>Decision</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((week) => (
                  <TableRow key={week.id}>
                    <TableCell className="whitespace-nowrap">{week.employee.displayName}</TableCell>
                    <TableCell className="ts-num whitespace-nowrap">
                      {formatRange(week.periodStart, week.periodEnd)}
                    </TableCell>
                    <TableCell className="ts-num text-right">
                      {formatDuration(week.actualSeconds ?? 0)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={week.status === 'APPROVED' ? 'secondary' : 'destructive'}>
                        {week.status === 'APPROVED' ? 'approved' : 'sent back'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <ConfirmButton
                        variant="outline"
                        size="sm"
                        disabled={reopen.isPending}
                        pending={reopen.isPending}
                        title={`Reopen ${week.employee.displayName}'s week?`}
                        description={
                          <>
                            {formatRange(week.periodStart, week.periodEnd)} becomes editable again
                            and they will need to submit it once more. They are emailed, and
                            the reopening is recorded under your name. No other week is
                            affected.
                          </>
                        }
                        confirmLabel="Reopen it"
                        onConfirm={() => void run(week)}
                      >
                        Reopen
                      </ConfirmButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** "14 – 20 Sep", collapsing what both ends share. */
function formatRange(from: string, to: string): string {
  const opts = { day: 'numeric', month: 'short', timeZone: 'UTC' } as const;
  const a = new Date(`${from}T00:00:00.000Z`);
  const b = new Date(`${to}T00:00:00.000Z`);
  const left =
    a.getUTCMonth() === b.getUTCMonth()
      ? String(a.getUTCDate())
      : a.toLocaleDateString('en-IN', opts);
  return `${left} – ${b.toLocaleDateString('en-IN', opts)}`;
}
